import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatToolbarModule } from '@angular/material/toolbar';
import { AdminService } from '../../../service/admin.service';
import { OptionDialogComponent, OptionDialogData, GamenightOption as DialogGamenightOption } from './dialogs/option-dialog/option-dialog.component';
import { DeleteConfirmationDialogComponent, DeleteConfirmationData } from './dialogs/delete-confirmation-dialog/delete-confirmation-dialog.component';

export interface GamenightOption {
  id: number;
  title: string;
  image_url: string;
  is_active: string;
  display_order: number;
  created_at: string;
  updated_at: string;
  vote_count: number;
}

@Component({
  selector: 'app-admin-gamenight',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatCardModule,
    MatChipsModule,
    MatToolbarModule
  ],
  templateUrl: './gamenight.component.html',
  styleUrl: './gamenight.component.less'
})
export class AdminGamenightComponent implements OnInit {
  adminService = inject(AdminService);
  dialog = inject(MatDialog);
  snackBar = inject(MatSnackBar);

  options: GamenightOption[] = [];
  topOptionIds: number[] = [];
  displayedColumns: string[] = ['title', 'vote_bar', 'is_active', 'display_order', 'actions'];
  isLoading = false;
  isResetting = false;


  ngOnInit() {
    this.loadOptions();
  }

  loadOptions() {
    this.isLoading = true;
    this.adminService.getGamenightOptions().subscribe({
      next: (options) => {
        console.log('Received options:', options);
        this.options = Array.isArray(options) ? options : [];
        
        const nonZeroVotes = this.options
          .map(o => o.vote_count)
          .filter(count => count > 0)
          .sort((a, b) => b - a);
          
        const threshold = nonZeroVotes.length > 1 ? nonZeroVotes[1] : nonZeroVotes[0] || 0;
        
        this.topOptionIds = this.options
          .filter(o => o.vote_count >= threshold && o.vote_count > 0)
          .map(o => o.id);

        this.isLoading = false;
      },
      error: (error) => {
        this.snackBar.open('Failed to load options', 'Close', { duration: 3000 });
        this.isLoading = false;
        console.error('Error loading options:', error);
      }
    });
  }

  startCreate() {
    const maxDisplayOrder = this.options.length > 0 ? Math.max(...this.options.map(o => o.display_order)) : 0;
    const dialogData: OptionDialogData = {
      isEdit: false,
      maxDisplayOrder: maxDisplayOrder
    };

    const dialogRef = this.dialog.open(OptionDialogComponent, {
      data: dialogData,
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.saveOption(result, 'create');
      }
    });
  }

  startEdit(option: GamenightOption) {
    const maxDisplayOrder = this.options.length > 0 ? Math.max(...this.options.map(o => o.display_order)) : 0;
    const dialogData: OptionDialogData = {
      option: {
        id: option.id,
        title: option.title,
        image_url: option.image_url,
        is_active: option.is_active === '1',
        display_order: option.display_order
      },
      isEdit: true,
      maxDisplayOrder: maxDisplayOrder
    };

    const dialogRef = this.dialog.open(OptionDialogComponent, {
      data: dialogData,
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.saveOption(result, 'update');
      }
    });
  }

  private saveOption(formData: DialogGamenightOption, action: 'create' | 'update') {
    const data: any = {
      ...formData,
      is_active: formData.is_active ? 1 : 0
    };

    this.adminService.manageGamenightOption(action, data).subscribe({
      next: (response) => {
        this.snackBar.open(
          action === 'update' ? 'Option updated successfully' : 'Option created successfully',
          'Close',
          { duration: 3000 }
        );
        this.loadOptions();
      },
      error: (error) => {
        this.snackBar.open('Failed to save option', 'Close', { duration: 3000 });
        console.error('Error saving option:', error);
      }
    });
  }

  deleteOption(option: GamenightOption) {
    const dialogData: DeleteConfirmationData = {
      title: 'Delete Option',
      message: `Are you sure you want to delete "${option.title}"? This will also delete all votes for this option.`,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    };

    const dialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
      data: dialogData,
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.adminService.manageGamenightOption('delete', { id: option.id }).subscribe({
          next: (response) => {
            this.snackBar.open('Option deleted successfully', 'Close', { duration: 3000 });
            this.loadOptions();
          },
          error: (error) => {
            this.snackBar.open('Failed to delete option', 'Close', { duration: 3000 });
            console.error('Error deleting option:', error);
          }
        });
      }
    });
  }

  resetAllVotes() {
    const dialogData: DeleteConfirmationData = {
      title: 'Reset All Votes',
      message: 'Are you sure you want to reset ALL votes? This action cannot be undone.',
      confirmText: 'Reset All',
      cancelText: 'Cancel'
    };

    const dialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
      data: dialogData,
      width: '400px'
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.isResetting = true;
        this.adminService.manageGamenightOption('reset_votes').subscribe({
          next: (response) => {
            this.snackBar.open('All votes have been reset', 'Close', { duration: 3000 });
            this.isResetting = false;
            this.loadOptions();
          },
          error: (error) => {
            this.snackBar.open('Failed to reset votes', 'Close', { duration: 3000 });
            this.isResetting = false;
            console.error('Error resetting votes:', error);
          }
        });
      }
    });
  }


  getTotalVotes(): number {
    if (!Array.isArray(this.options)) {
      return 0;
    }
    return this.options.reduce((total, option) => total + option.vote_count, 0);
  }

  getActiveOptionsCount(): number {
    if (!Array.isArray(this.options)) {
      return 0;
    }
    return this.options.filter(o => o.is_active === '1').length;
  }

  getTopVotes(): number {
    if (!Array.isArray(this.options) || this.options.length === 0) {
      return 0;
    }
    return this.options.reduce((total, option) => Math.max(total, option.vote_count), 0);
  }

  getVotePercentage(voteCount: number): number {
    const maxVotes = this.getTopVotes();
    if (maxVotes === 0) {
      return 0;
    }
    return Math.round((voteCount / maxVotes) * 100);
  }
}
